import { useMemo, useCallback } from "react";

import type { Dropdown } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import { handleCTAClick, TestCases } from "app/common/utils/handleGainAccessRedirect";
import { useAddonsState } from "app/contexts/AddonStateContext/AddonStateContext";
import { ADDON_TYPE_NAME_DICT, AddOnType } from "app/contexts/AddonStateContext/const";
import { useNoTouchPurchaseContext } from "app/contexts/NoTouchPurchaseContext/NoTouchPurchaseContext";
import { useCountries } from "app/marketing/hooks/use-countries/useCountries";
import { openHooksModalV5 } from "app/services/ModalService";
import { EHooksVersions } from "app/services/SwHook";
import { goToAddonsPage } from "app/UtilitiesAndConstants/UtilityFunctions/goToAddonsPage";
import { goToPackagesPage } from "app/UtilitiesAndConstants/UtilityFunctions/goToPackagesPage";
import {
    hasAddonsEnabled,
    isNoTouch,
    isTrialOrAfterTrial,
} from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

interface ILockedItemCallbacks {
    onLockedItemClick?: (countryCode: string) => void;
    onCrownLockedClick?: () => void;
    hasCountryFilterInAvailableTypes: boolean;
}

interface IUseCountryFilterLockedClickProps {
    dropdownRef: React.RefObject<Dropdown<unknown> | null>;
}

interface IUseCountryFilterLockedClickApi {
    openContactSales?: () => void;
    lockedItemCallbacks?: ILockedItemCallbacks;
}

export const useCountryFilterLockedClick = ({
    dropdownRef,
}: IUseCountryFilterLockedClickProps): IUseCountryFilterLockedClickApi => {
    const { getIsAddonAvailable, getAddon } = useAddonsState();
    const { countries } = useCountries();
    const { handlePurchaseAddonClick } = useNoTouchPurchaseContext();

    const isCountryFilterAvailable = getIsAddonAvailable(AddOnType.CountryFilter);
    const isAvailableProductsEnabled = hasAddonsEnabled(swSettings) || isTrialOrAfterTrial(swSettings);
    const hasCountryFilterInAvailableTypes =
        !isAvailableProductsEnabled || getAddon(AddOnType.CountryFilter) !== undefined;
    const shouldOpenOneClickPurchase = isNoTouch(swSettings) && isCountryFilterAvailable;
    const shouldRedirectToPackages = isTrialOrAfterTrial(swSettings);

    const resolveCountry = useCallback(
        (countryCode: string) => countries.find((country) => country.code === countryCode),
        [countries],
    );

    const onLockedItemClick = useMemo(() => {
        if (shouldRedirectToPackages) {
            // Page redirects (goToAddonsPage's packageId) use the alpha-2 code, e.g. CountryFilter${alpha2Code}
            return (countryCode: string) => {
                const alpha2Code = resolveCountry(countryCode)?.alpha2;
                goToPackagesPage({ presetId: alpha2Code ? `CountryFilter${alpha2Code}` : "CountryFilter" });
            };
        }

        if (shouldOpenOneClickPurchase) {
            // The one-click purchase modal matches by alpha-3 code (see NoTouchPurchaseSummaryModal's getSelectedAddon)
            return (countryCode: string) => {
                const alpha3Code = resolveCountry(countryCode)?.alpha3;

                if (!alpha3Code) {
                    goToAddonsPage({});
                    return;
                }

                handlePurchaseAddonClick(ADDON_TYPE_NAME_DICT.CountryFilterDynamic, 1, alpha3Code);
                // @ts-expect-error closePopup exists on Dropdown but is typed as private
                dropdownRef.current?.closePopup();
            };
        }

        return undefined;
    }, [shouldRedirectToPackages, shouldOpenOneClickPurchase, resolveCountry, handlePurchaseAddonClick, dropdownRef]);

    const openContactSales = useCallback(() => {
        // @ts-expect-error closePopup exists on Dropdown but is typed as private
        dropdownRef.current?.closePopup();
        void openHooksModalV5("ContactUs", undefined, "ContactUsCountryFilter", "GetLoading");
    }, [dropdownRef]);

    const onCrownLockedClick = useCallback(() => {
        void handleCTAClick(EHooksVersions.V4, "WebCountry", TestCases.TRIAL_AFTER_TRIAL);
    }, []);

    const lockedItemCallbacks = {
        onLockedItemClick,
        onCrownLockedClick,
        hasCountryFilterInAvailableTypes,
    };

    return {
        openContactSales,
        lockedItemCallbacks,
    };
};
